import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalyticsContext } from '../contexts/AnalyticsContext';

// Active markets in Shopify (priority order)
export const ACTIVE_LANGUAGES = {
  en: { name: 'English', flag: '🇬🇧', market: 'primary' },
  de: { name: 'Deutsch', flag: '🇩🇪', market: 'expansion' },
  pl: { name: 'Polski', flag: '🇵🇱', market: 'expansion' },
  uk: { name: 'Українська', flag: '🇺🇦', market: 'expansion' },
} as const;

export type ActiveLanguage = keyof typeof ACTIVE_LANGUAGES;

// Browser language to active language mapping
const BROWSER_LANGUAGE_MAP: Record<string, ActiveLanguage> = {
  en: 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'en-CA': 'en',
  'en-AU': 'en',
  de: 'de',
  'de-DE': 'de',
  'de-AT': 'de',
  'de-CH': 'de',
  pl: 'pl',
  'pl-PL': 'pl',
  uk: 'uk',
  'uk-UA': 'uk',
  // European fallbacks to nearest market
  fr: 'de',
  'fr-FR': 'de',
  'fr-BE': 'de',
  'fr-CH': 'de',
  it: 'de',
  'it-IT': 'de',
  'it-CH': 'de',
  es: 'de',
  'es-ES': 'de',
  nl: 'de',
  'nl-NL': 'de',
  'nl-BE': 'de',
  cs: 'de',
  'cs-CZ': 'de',
  sk: 'de',
  'sk-SK': 'de',
  hu: 'de',
  'hu-HU': 'de',
  // Eastern Europe to Polish/Ukrainian
  ru: 'uk',
  'ru-RU': 'uk',
  be: 'uk',
  'be-BY': 'uk',
  lt: 'pl',
  'lt-LT': 'pl',
  lv: 'pl',
  'lv-LV': 'pl',
  et: 'pl',
  'et-EE': 'pl',
};

function detectBrowserLanguage(): ActiveLanguage {
  // Check browser languages in order of preference
  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    const mappedLang =
      BROWSER_LANGUAGE_MAP[lang] || BROWSER_LANGUAGE_MAP[lang.split('-')[0]];
    if (mappedLang) {
      return mappedLang;
    }
  }

  return 'en'; // Default fallback
}

function getLanguageFromUrl(pathname: string): ActiveLanguage | null {
  const segments = pathname.split('/').filter(Boolean);
  const potentialLang = segments[0];

  if (potentialLang && potentialLang in ACTIVE_LANGUAGES) {
    return potentialLang as ActiveLanguage;
  }

  return null;
}

export function useLanguage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const analytics = useAnalyticsContext();
  const [currentLanguage, setCurrentLanguage] = useState<ActiveLanguage>('en');

  useEffect(() => {
    
    // Priority: URL > localStorage > browser > fallback
    const urlLanguage = getLanguageFromUrl(location.pathname);
    const storedLanguage = localStorage.getItem(
      'olispices-language'
    ) as ActiveLanguage;
    const browserLanguage = detectBrowserLanguage();


    let detectedLanguage: ActiveLanguage;

    if (urlLanguage) {
      // URL has language prefix (/de/, /pl/, /uk/)
      detectedLanguage = urlLanguage;
    } else {
      // No language prefix - use localStorage or browser detection
      detectedLanguage =
        (storedLanguage && storedLanguage in ACTIVE_LANGUAGES
          ? storedLanguage
          : null) || browserLanguage;
    }

    setCurrentLanguage(detectedLanguage);

    // Sync with i18next
    if (i18n.language !== detectedLanguage) {
      i18n.changeLanguage(detectedLanguage);
    }

    // Update localStorage when language changes
    if (detectedLanguage !== storedLanguage) {
      localStorage.setItem('olispices-language', detectedLanguage);
    }
  }, [location.pathname, i18n]);

  const changeLanguage = (newLanguage: ActiveLanguage, skipNavigation = false) => {
    const oldLanguage = currentLanguage;

    // Track language change only if it's a user-initiated change
    if (!skipNavigation) {
      analytics.trackLanguageChange(oldLanguage, newLanguage);
    }

    localStorage.setItem('olispices-language', newLanguage);
    setCurrentLanguage(newLanguage);

    // Sync with i18next for immediate UI updates
    i18n.changeLanguage(newLanguage);

    // Skip URL navigation if this is just syncing from URL
    if (skipNavigation) {
      return;
    }

    // Update URL structure: /de/product/kurkuma
    const currentPath = location.pathname;
    const segments = currentPath.split('/').filter(Boolean);

    // Remove existing language prefix if present
    if (segments[0] && segments[0] in ACTIVE_LANGUAGES) {
      segments.shift();
    }

    // Add new language prefix (except for English as default)
    const newPath =
      newLanguage === 'en'
        ? '/' + segments.join('/')
        : '/' + newLanguage + '/' + segments.join('/');
    
    
    navigate(newPath + location.search);
  };

  const getLocalizedUrl = (
    path: string,
    language: ActiveLanguage = currentLanguage
  ): string => {
    const basePath = path.startsWith('/') ? path : '/' + path;
    return language === 'en' ? basePath : '/' + language + basePath;
  };

  const getAllLanguageUrls = (path: string): Record<ActiveLanguage, string> => {
    return Object.keys(ACTIVE_LANGUAGES).reduce(
      (urls, lang) => {
        urls[lang as ActiveLanguage] =
          `https://olispices.com${getLocalizedUrl(path, lang as ActiveLanguage)}`;
        return urls;
      },
      {} as Record<ActiveLanguage, string>
    );
  };

  return {
    currentLanguage,
    availableLanguages: ACTIVE_LANGUAGES,
    changeLanguage,
    getLocalizedUrl,
    getAllLanguageUrls,
    isLanguageActive: (lang: string): lang is ActiveLanguage =>
      lang in ACTIVE_LANGUAGES,
    isInitialized: i18n.isInitialized,
  };
}
